import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  confirmPasswordReset,
  signOut,
  updateProfile,
  sendEmailVerification,
  applyActionCode,
  verifyPasswordResetCode
} from 'firebase/auth';
import firebaseConfig from './config';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

/**
 * Firebase Authentication Service
 */
const firebaseAuth = {
  /**
   * Sign in with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise} - User credential
   */
  login: async (email, password) => {
    try {
      console.log(`[firebase/auth.js login] Attempting login. Email: ${email}, Password length: ${password?.length}, Email type: ${typeof email}`);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return {
        user: userCredential.user,
        success: true
      };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Create a new user account
   * @param {string} email - User email
   * @param {string} password - User password
   * @param {string} name - User display name
   * @returns {Promise} - User credential
   */
  register: async (email, password, name) => {
    try {
      console.log(`[auth.js] Attempting to register with email: |${email}|, password length: ${password ? password.length : 'undefined'}, name: |${name}|`);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update user profile with display name
      await updateProfile(userCredential.user, {
        displayName: name
      });
      
      // Send email verification
      await sendEmailVerification(userCredential.user);
      
      return {
        user: userCredential.user,
        success: true
      };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Send password reset email
   * @param {string} email - User email
   * @returns {Promise} - Success status
   */
  sendPasswordResetEmail: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return {
        success: true
      };
    } catch (error) {
      console.error('Password reset email error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Verify password reset code
   * @param {string} code - Password reset code
   * @returns {Promise} - Email associated with the code
   */
  verifyPasswordResetCode: async (code) => {
    try {
      const email = await verifyPasswordResetCode(auth, code);
      return {
        email,
        success: true
      };
    } catch (error) {
      console.error('Verify reset code error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Confirm password reset with new password
   * @param {string} code - Password reset code
   * @param {string} newPassword - New password
   * @returns {Promise} - Success status
   */
  confirmPasswordReset: async (code, newPassword) => {
    try {
      await confirmPasswordReset(auth, code, newPassword);
      return {
        success: true
      };
    } catch (error) {
      console.error('Confirm password reset error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Apply action code (email verification, password reset)
   * @param {string} code - Action code
   * @returns {Promise} - Success status
   */
  verifyEmail: async (code) => {
    try {
      await applyActionCode(auth, code);
      return {
        success: true
      };
    } catch (error) {
      console.error('Email verification error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Resend verification email to current user
   * @returns {Promise} - Success status
   */
  resendVerificationEmail: async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return {
          success: false,
          error: 'No user is currently signed in.'
        };
      }

      await sendEmailVerification(currentUser);
      return {
        success: true
      };
    } catch (error) {
      console.error('Resend verification email error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Sign out the current user
   * @returns {Promise} - Success status
   */
  logout: async () => {
    try {
      await signOut(auth);
      return {
        success: true
      };
    } catch (error) {
      console.error('Logout error:', error);
      return {
        success: false,
        error: mapAuthErrorToMessage(error.code)
      };
    }
  },

  /**
   * Get the current user
   * @returns {Object|null} - Current user or null if not signed in
   */
  getCurrentUser: () => {
    return auth.currentUser;
  },

  /**
   * Set up an auth state change listener
   * @param {Function} callback - Callback function to handle auth state changes
   * @returns {Function} - Unsubscribe function
   */
  onAuthStateChanged: (callback) => {
    return auth.onAuthStateChanged(callback);
  }
};

/**
 * Map Firebase auth error codes to user-friendly messages
 * @param {string} errorCode - Firebase auth error code
 * @returns {string} - User-friendly error message
 */
const mapAuthErrorToMessage = (errorCode) => {
  const errorMessages = {
    'auth/email-already-in-use': 'This email is already in use. Please try another one or sign in.',
    'auth/invalid-email': 'The email address is not valid.',
    'auth/user-disabled': 'This account has been disabled. Please contact support.',
    'auth/user-not-found': 'No account found with this email address.',
    'auth/wrong-password': 'Incorrect password. Please try again.',
    'auth/weak-password': 'Password is too weak. Please use a stronger password.',
    'auth/operation-not-allowed': 'This operation is not allowed.',
    'auth/invalid-action-code': 'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.',
    'auth/expired-action-code': 'The action code has expired.',
    'auth/invalid-verification-code': 'The verification code is invalid.',
    'auth/missing-verification-code': 'The verification code is missing.',
    'auth/quota-exceeded': 'Quota exceeded. Please try again later.',
    'auth/requires-recent-login': 'This operation requires recent authentication. Please sign in again before retrying.',
    'auth/too-many-requests': 'Too many unsuccessful login attempts. Please try again later.',
    'auth/network-request-failed': 'A network error has occurred. Please check your connection and try again.',
    'auth/internal-error': 'An internal error has occurred. Please try again later.',
    'auth/popup-closed-by-user': 'The popup was closed before authentication was completed.',
    'auth/unauthorized-domain': 'This domain is not authorized for OAuth operations.',
    'auth/invalid-credential': 'The provided credential is invalid or has expired.',
    'auth/invalid-continue-uri': 'The continue URL provided is invalid.',
    'auth/invalid-phone-number': 'The phone number is invalid.',
    'auth/missing-phone-number': 'The phone number is missing.',
    'auth/invalid-recipient-email': 'The recipient email is invalid.',
    'auth/invalid-sender': 'The email template sender is invalid.',
    'auth/missing-continue-uri': 'A continue URL must be provided in the request.',
    'auth/missing-iframe-start': 'An internal error has occurred.',
    'auth/missing-ios-bundle-id': 'The iOS bundle ID is missing.',
    'auth/missing-or-invalid-nonce': 'The OIDC nonce is missing or invalid.',
    'auth/missing-android-pkg-name': 'The Android package name is missing.',
    'auth/auth-domain-config-required': 'Be sure to include authDomain when calling firebase.initializeApp().',
    'auth/cancelled-popup-request': 'This operation has been cancelled due to another conflicting popup being opened.',
    'auth/captcha-check-failed': 'The reCAPTCHA response token provided is either invalid, expired, or already used.',
    'auth/credential-already-in-use': 'This credential is already associated with a different user account.',
    'auth/custom-token-mismatch': 'The custom token corresponds to a different audience.',
    'auth/missing-custom-token': 'The custom token is missing.',
    'auth/invalid-custom-token': 'The custom token format is incorrect.',
    'auth/invalid-email-verified': 'The email verified property must be a boolean.',
    'auth/invalid-oauth-provider': 'The OAuth provider ID provided is invalid.',
    'auth/invalid-oauth-client-id': 'The OAuth client ID provided is invalid.',
    'auth/invalid-api-key': 'Your API key is invalid, please check you have copied it correctly.',
    'auth/invalid-cert-hash': 'The SHA-1 certificate hash provided is invalid.',
    'auth/invalid-user-token': 'The user\'s credential is no longer valid. The user must sign in again.',
    'auth/token-expired': 'The user\'s credential has expired. The user must sign in again.',
    'auth/app-deleted': 'This instance of FirebaseApp has been deleted.',
    'auth/account-exists-with-different-credential': 'An account already exists with the same email address but different sign-in credentials. Sign in using a provider associated with this email address.',
    'auth/invalid-persistence-type': 'The specified persistence type is invalid.',
    'auth/unsupported-persistence-type': 'The current environment does not support the specified persistence type.',
    'auth/invalid-argument': 'An invalid argument was provided to an Authentication method.',
    'auth/invalid-creation-time': 'The creation time must be a valid UTC date.',
    'auth/invalid-disabled-field': 'The disabled field must be a boolean.',
    'auth/invalid-display-name': 'The display name must be a non-empty string.',
    'auth/invalid-id-token': 'The provided ID token is not a valid Firebase ID token.',
    'auth/invalid-last-sign-in-time': 'The last sign-in time must be a valid UTC date.',
    'auth/invalid-password': 'The provided value for the password user property is invalid. It must be a string with at least six characters.',
    'auth/invalid-photo-url': 'The provided value for the photoURL user property is invalid.',
    'auth/invalid-provider-id': 'The providerId must be a valid supported provider identifier.',
    'auth/invalid-session-cookie-duration': 'The session cookie duration must be a valid number in milliseconds between 5 minutes and 2 weeks.',
    'auth/invalid-uid': 'The provided uid must be a non-empty string with at most 128 characters.',
    'auth/maximum-user-count-exceeded': 'The maximum allowed number of users to import has been exceeded.',
    'auth/missing-uid': 'A uid identifier is required for the current operation.',
    'auth/session-cookie-expired': 'The provided Firebase session cookie is expired.',
    'auth/session-cookie-revoked': 'The Firebase session cookie has been revoked.',
    'auth/uid-already-exists': 'The provided uid is already in use by an existing user.',
    'auth/email-already-exists': 'The provided email is already in use by an existing user.',
    'auth/phone-number-already-exists': 'The provided phone number is already in use by an existing user.',
    'auth/project-not-found': 'No Firebase project was found for the provided credential.',
    'auth/insufficient-permission': 'The credential used to initialize the Admin SDK has insufficient permission to access the requested Authentication resource.',
    'auth/rejected-credential': 'The credential used to authenticate the Admin SDKs cannot be used to perform the desired action.',
  };

  return errorMessages[errorCode] || 'An unknown error occurred. Please try again.';
};

export { firebaseAuth };
